// React
import { useEffect, useState } from "react";
import {
  Link as ReactRouterLink,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom"
import React from "react"

// Hooks and Redux
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { refreshMasterData } from "../../../redux/slices/masterDataSlice"

// MUI Components
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import CircularProgress from "@mui/material/CircularProgress"
import Breadcrumbs from "@mui/material/Breadcrumbs"
import Link from "@mui/material/Link"

// MUI Icons
import HomeIcon from "@mui/icons-material/Home"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import EditIcon from "@mui/icons-material/Edit"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"

// Utils
import { ToastContainer, toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import jwtDecode from "jwt-decode"
import Swal from "sweetalert2"

// Styles
import { green } from "@mui/material/colors"

// Types
import { TokenPayload } from "../../../@types"

// Services
import {
  createBank,
  updateBank,
  getBankById,
} from "../../../services/MasterDataService"

interface BankFormData {
  bank_code: string
  bank_name: string
  is_active: boolean
}

type ModeType = "add" | "edit"

interface MyEditorProps {
  type: ModeType
}

const ManageBank: React.FC<MyEditorProps> = ({ type }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const bankData = location?.state?.bankData

  // Auto-detect mode from URL params
  const isEdit = Boolean(id)

  const dispatch = useAppDispatch()

  const userRoleType = useAppSelector((state) => state.user.roletype)
  const havePermission =
    userRoleType === "Super Admin"

  const [formData, setFormData] = useState<BankFormData>({
    bank_code: "",
    bank_name: "",
    is_active: true,
  })

  const [dataLoading, setDataLoading] = useState<boolean>(isEdit) // only load on edit
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  if (!havePermission) {
    navigate("/forbidden", { replace: true })
  }

  // ── Fetch existing bank data when editing ──────────────────────────────────
  useEffect(() => {
    if (!isEdit || !id) return

    if (type === "edit" && bankData) {
      setDataLoading(false)
      setFormData(bankData)
    }

    // fetchBank()
  }, [id, isEdit, navigate, i18n.language])

  // ── Validation ─────────────────────────────────────────────────────────────
  const formValidationSchema = Yup.object().shape({
    ...(isEdit
      ? {}
      : {
          bank_code: Yup.string()
            .required(`${t("Bank code is required")}`)
            .min(3, `${t("Bank code must be at least 3 characters")}`)
            .max(3, `${t("Bank code must be at most 3 characters")}`)
            .matches(
              /^[a-zA-Z]+$/,
              `${t("Company code must be english only")}`
            ),
        }),
    bank_name: Yup.string().required(`${t("Bank name is required")}`),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(formValidationSchema) })

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, is_active: event.target.checked }))
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  const onSubmit = (values: any) => {
    const submit = async () => {
      try {
        const parsedToken: TokenPayload = jwtDecode(localStorage.access_token)
        setSubmitLoading(true)

        let response

        if (isEdit) {
          const editData = {
            bank_code: formData.bank_code,
            bank_name: values.bank_name,
            is_active: formData.is_active,
            modified_by: parsedToken.Username,
          }
          response = await updateBank(id as string, editData)
        } else {
          const createData = {
            bank_code: values.bank_code,
            bank_name: values.bank_name,
            is_active: formData.is_active,
            created_by: parsedToken.Username,
          }
          response = await createBank(createData)
        }

        if (response) {
          setSubmitLoading(false)
          const responseData = response?.data
          if (!responseData?.success) {
            toast.error(
              i18n.language === "th"
                ? responseData.message_th
                : responseData.message_en,
              {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: "light",
              }
            )
          } else {
            Swal.fire({
              title:
                i18n.language === "th"
                  ? responseData.message_th
                  : responseData.message_en,
              icon: "success",
              showCancelButton: false,
              confirmButtonColor: "#43A047",
              confirmButtonText: "OK!",
              allowOutsideClick: false,
              allowEscapeKey: false,
            }).then((result) => {
              if (result.isConfirmed) {
                dispatch(refreshMasterData())
              navigate("/admin/masterdata/bank", { replace: true })
              }
            })
          }
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      }
    }

    submit()
  }

  // ── Loading skeleton (edit mode fetching data) ─────────────────────────────
  if (dataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <ToastContainer style={{ width: "400px" }} />

      {havePermission && (
        <>
          {/* ── Back + Breadcrumbs ─────────────────────────────────────── */}
          <Box sx={{ height: "auto", maxWidth: "100%", mt: 2 }}>
            <Box display="flex" gap={3} sx={{ mb: 3 }}>
              <Box
                display="flex"
                component={ReactRouterLink}
                onClick={() => window.history.back()}
                sx={{
                  textDecoration: "none",
                  color: "#0d47a1",
                  alignItems: "center",
                  ":hover": { color: "#1976d2" },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
                <Typography sx={{ fontWeight: "bold" }}>{t("Back")}</Typography>
              </Box>

              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin"
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Dashboard")}
                </Link>
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  color="inherit"
                  component={ReactRouterLink}
                  to="/admin/masterdata/bank"
                >
                  <AccountBalanceIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {t("Bank")}
                </Link>
                <Typography
                  sx={{ display: "flex", alignItems: "center" }}
                  color="text.primary"
                >
                  {isEdit ? (
                    <>
                      <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                      {t("Edit Bank")}
                    </>
                  ) : (
                    <>
                      <AddCircleOutlineIcon
                        sx={{ mr: 0.5 }}
                        fontSize="inherit"
                      />
                      {t("Create New Bank")}
                    </>
                  )}
                </Typography>
              </Breadcrumbs>
            </Box>
          </Box>

          {/* ── Page title ────────────────────────────────────────────── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "70px",
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", textAlign: "left" }}
            >
              {isEdit ? t("Edit Bank") : t("Create New Bank")}
            </Typography>
          </Box>

          {/* ── Form ──────────────────────────────────────────────────── */}
          <Box
            component="form"
            noValidate
            sx={{ mt: 3 }}
            onKeyDown={(e: React.KeyboardEvent) =>
              e.key === "Enter" && e.preventDefault()
            }
          >
            <Grid container spacing={3}>
              {/* Bank Code */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="bankCode"
                  label={t("Bank Code")}
                  disabled={isEdit} // readonly when editing
                  {...(!isEdit ? register("bank_code") : {})}
                  error={!!errors.bank_code}
                  value={isEdit ? formData.bank_code : undefined}
                  onChange={
                    isEdit
                      ? undefined
                      : (event: React.ChangeEvent<HTMLInputElement>) => {
                          setFormData((prev) => ({
                            ...prev,
                            bank_code: event.target.value,
                          }))
                          ;(errors as any).bank_code = undefined
                        }
                  }
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {errors.bank_code?.message}
                </Typography>
              </Grid>

              {/* Bank Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="bankName"
                  label={t("Bank Name")}
                  {...register("bank_name")}
                  error={!!errors.bank_name}
                  value={formData.bank_name}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      bank_name: event.target.value,
                    }))
                    ;(errors as any).bank_name = undefined
                  }}
                />
                <Typography
                  variant="inherit"
                  color="error"
                  align="left"
                  pt={1}
                  fontSize={13}
                >
                  {errors.bank_name?.message}
                </Typography>
              </Grid>

              {/* Active checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  label={t("Active")}
                  control={
                    <Checkbox
                      checked={formData.is_active}
                      onChange={handleActiveChange}
                      inputProps={{ "aria-label": "Active" }}
                    />
                  }
                />
              </Grid>
            </Grid>

            {/* Action buttons */}
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  sx={{ mt: 5, mb: 2, color: "white" }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={submitLoading}
                >
                  {isEdit ? t("Save") : t("Create")}
                  {submitLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: green[500],
                        position: "absolute",
                        zIndex: 999,
                      }}
                    />
                  )}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 5, mb: 2 }}
                  component={ReactRouterLink}
                  to="/admin/masterdata/bank"
                >
                  {t("Cancel")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  )
}

export default ManageBank;
 